import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
    ILiveScoreInitPayload,
    ILiveScoreInitPayloadByTour,
    ILiveScoreLocations,
    ILiveScoreReducer,
    ILiveScoreSidebar,
    LiveScoreHeader,
    LiveScoreRootObject,
    LiveScoreSidebar,
    LiveScoreStatistics,
    LiveScoreTimelines,
    LiveScoreTurnShots,
    OnShotForLiveScore,
    ShotUpdateSocketData
} from "./types";
import { API_LIVE_SCORE_INIT, API_LIVE_SCORE_INIT_BY_TOUR, API_LIVE_SCORE_SIDEBAR } from "../../../services/api/api.urls";
import {toastr} from "react-redux-toastr";
import { api } from "../../../services/api/api.service";
import {formatSideBar} from "./helpers/formatSideBar";
import moment from "moment/moment";
import { TryCatch } from "../../../services/TryCatch";
import { COMPANY_ABBR } from "../../../common/constants/company";

const initialState: ILiveScoreReducer = {
    allInformation: {
        liveScore: {
            header: {
                gameId: 0,
                legNum: 0,
                legs: [],
                scores: [],
                players: [
                    {
                        id: 0,
                        name: ""
                    },
                    {
                        id: 0,
                        name: ""
                    }
                ],
                nextPlayerId: 0,
                status: 2,
                turnShots: {} as LiveScoreTurnShots
            },
            timelines: {} as LiveScoreTimelines,
            statistics: {
                gameId: 0,
                statistics: [],
                players: [],
                groups: []
            }
        },
        sidebar: {} as ILiveScoreSidebar,
        sidebarEvents: {},
        gameStartDate: ''
    },
    lastShot: {} as OnShotForLiveScore,
    liveScoreLoader: false,
    activeSideBarEvent: 0,
    isAdc: false
}

export const getLiveScoreInfo = createAsyncThunk<LiveScoreRootObject, ILiveScoreInitPayload, { rejectValue: string }>(
    'liveScoreInfo/schedule',
    async (payload, { rejectWithValue }) => {
        const { gameId, gameType } = payload;
        const result = await TryCatch(
            () => api.get(`${API_LIVE_SCORE_INIT}/${gameType}/${gameId}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR,"Live score info failed.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getLiveScoreInfoByTour = createAsyncThunk<LiveScoreRootObject, ILiveScoreInitPayloadByTour, { rejectValue: string }>(
    'liveScoreInfoByTour/schedule',
    async (payload, {rejectWithValue}) => {
        const {tourId} = payload;
        const result = await TryCatch(
            () => api.get(`${API_LIVE_SCORE_INIT_BY_TOUR}/${tourId}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Live score info failed.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const changeGameLiveScore = createAsyncThunk<LiveScoreRootObject, ILiveScoreInitPayload, { rejectValue: string }>(
    'liveScoreInfo/changeGame',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_LIVE_SCORE_INIT}/${payload.gameType}/${payload.gameId}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Live score info failed.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const updateSidebar = createAsyncThunk<ILiveScoreLocations[], string, { rejectValue: string }>(
    'liveScoreInfo/updateSideBar',
    async (date, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_LIVE_SCORE_SIDEBAR}/${moment(date).format("YYYY-MM-DD")}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Live score info failed.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const liveScoreInfoSlice = createSlice({
    name: 'liveScoreInfo',
    reducers: {
        setIsADC: (state, action) => {
            state.isAdc = action.payload
        },
        setLastShot: (state, action: PayloadAction<ShotUpdateSocketData>) => {
            const {bed, ring, message} = action.payload;
            if (state.allInformation.liveScore.header.gameId === action.payload.gameId) {
                state.lastShot = {bed, ring, message}
            } else {
                return state
            }
        },
        setLiveScoreHeader: (state, action: PayloadAction<LiveScoreHeader>) => {
            if (state.allInformation.liveScore.header.gameId === action.payload.gameId) {
                state.allInformation.liveScore.header = action.payload
            } else {
                return state
            }

        },

        setLiveScoreTimelines: (state, action: PayloadAction<LiveScoreTimelines>) => {
            if (state.allInformation.liveScore.header.gameId === action.payload.gameId) {
                state.allInformation.liveScore.timelines = action.payload
            } else {
                return state
            }

        },

        setLiveScoreStatistic: (state, action: PayloadAction<LiveScoreStatistics>) => {
            if (state.allInformation.liveScore.header.gameId === action.payload.gameId) {
                state.allInformation.liveScore.statistics = action.payload
            } else {
                return state
            }

        },

        setSidebar: (state, action: PayloadAction<LiveScoreSidebar>) => {
            state.allInformation.sidebarEvents = formatSideBar(action.payload.locations);
        }
    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getLiveScoreInfo.pending, (state: ILiveScoreReducer) => {
                state.liveScoreLoader = true;
            })
            .addCase(getLiveScoreInfo.fulfilled, (state: ILiveScoreReducer, action) => {
                state.allInformation.liveScore = {
                    header: action.payload.header,
                    statistics: action.payload.statistics,
                    timelines: action.payload.timelines
                };
                state.allInformation.sidebar = action.payload.sidebar;
                state.activeSideBarEvent = action.meta.arg.gameId;
                state.liveScoreLoader = false;
                if (action.payload.sidebar) {
                    state.allInformation.sidebarEvents = formatSideBar(action.payload.sidebar.locations);
                }

            })
            .addCase(getLiveScoreInfo.rejected, (state: ILiveScoreReducer, action) => {
                console.log(action.payload);
                state.liveScoreLoader = false;
            })
            .addCase(getLiveScoreInfoByTour.pending, (state: ILiveScoreReducer) => {
                state.liveScoreLoader = true;
            })
            .addCase(getLiveScoreInfoByTour.fulfilled, (state: ILiveScoreReducer, action) => {
                state.allInformation.liveScore = {
                    header: action.payload.header,
                    statistics: action.payload.statistics,
                    timelines: action.payload.timelines
                };
                state.allInformation.sidebar = action.payload.sidebar;
                state.liveScoreLoader = false;
                if (action.payload.sidebar) {
                    state.allInformation.sidebarEvents = formatSideBar(action.payload.sidebar.locations);
                }
            })
            .addCase(getLiveScoreInfoByTour.rejected, (state: ILiveScoreReducer) => {
                state.liveScoreLoader = false;
            })
            .addCase(changeGameLiveScore.pending, (state: ILiveScoreReducer) => {
                state.liveScoreLoader = true;
            })
            .addCase(changeGameLiveScore.fulfilled, (state: ILiveScoreReducer, action) => {
                state.liveScoreLoader = false;
                state.allInformation.liveScore = {
                    header: action.payload.header,
                    statistics: action.payload.statistics,
                    timelines: action.payload.timelines
                };
                state.activeSideBarEvent = action.meta.arg.gameId;
                state.liveScoreLoader = false;
            })
            .addCase(changeGameLiveScore.rejected, (state: ILiveScoreReducer) => {
                state.liveScoreLoader = false;
            })
            .addCase(updateSidebar.fulfilled, (state: ILiveScoreReducer, action) => {
                if (action.payload) {
                    state.allInformation.sidebarEvents = formatSideBar(action.payload);
                }
            })
    }
})

export const {
    setLastShot,
    setSidebar,
    setLiveScoreHeader,
    setLiveScoreTimelines,
    setLiveScoreStatistic,
    setIsADC
} = liveScoreInfoSlice.actions;
export default liveScoreInfoSlice.reducer;
